import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfigurationsCmSheets from "../services/api/configurations/ConfigurationsCmSheets.service";
import {
  CONFIG_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { useNotification } from "./notification.hook";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import ConfigurationsCollaboratorsService from "../services/api/configurations/ConfigurationsCollaborators.service";
import { useEffect } from "react";

const MANAGE_ARCHIVING_STATS_QUERY_KEY = "get-config-cm-sheets-stats";

export const useArchivingStats = () => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [MANAGE_ARCHIVING_STATS_QUERY_KEY],
    queryFn: ConfigurationsCmSheets.getStats,
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useArchiveAllCmSheets = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["archive-all-cm-sheets"],
    mutationFn: ConfigurationsCmSheets.archiveAll,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MANAGE_ARCHIVING_STATS_QUERY_KEY],
      });
    },
  });

  return { mutateAsync, isPending };
};

export const useSolicitAllCm = () => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["solicit-all-cm"],
    mutationFn: ConfigurationsCmSheets.solicitAll,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MANAGE_ARCHIVING_STATS_QUERY_KEY],
      });
    },
    onError: (error) => {
      console.error(`error sending cm sheet urges ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface usePendingFinalFeedbackCollaboratorsProps {
  pageNumberPendingFinalFeedback: number;
  searchDebouncedValue: string;
  selectedPracticePendingFinalFeedback: string | null;
}

export const usePendingFinalFeedbackCollaborators = ({
  pageNumberPendingFinalFeedback,
  searchDebouncedValue,
  selectedPracticePendingFinalFeedback,
}: usePendingFinalFeedbackCollaboratorsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-pending-config-collaborators",
      pageNumberPendingFinalFeedback,
      searchDebouncedValue,
      selectedPracticePendingFinalFeedback,
    ],
    queryFn: ({ signal }) =>
      ConfigurationsCollaboratorsService.list(
        CONFIG_COLLABORATORS_PAGE_SIZE,
        pageNumberPendingFinalFeedback,
        [
          ...(selectedPracticePendingFinalFeedback
            ? [
                {
                  key: "practice",
                  value: [selectedPracticePendingFinalFeedback],
                },
              ]
            : []),
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
          { key: "isFinalFeedback", value: [false] },
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useReadySendSheetCollaboratorsProps {
  pageNumberReadySendSheet: number;
  searchDebouncedValue: string;
  selectedPracticeReadySendSheet: string | null;
}

export const useReadySendSheetCollaborators = ({
  pageNumberReadySendSheet,
  searchDebouncedValue,
  selectedPracticeReadySendSheet,
}: useReadySendSheetCollaboratorsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-ready-config-collaborators",
      pageNumberReadySendSheet,
      searchDebouncedValue,
      selectedPracticeReadySendSheet,
    ],
    queryFn: ({ signal }) =>
      ConfigurationsCollaboratorsService.list(
        CONFIG_COLLABORATORS_PAGE_SIZE,
        pageNumberReadySendSheet,
        [
          ...(selectedPracticeReadySendSheet
            ? [
                {
                  key: "practice",
                  value: [selectedPracticeReadySendSheet],
                },
              ]
            : []),
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
          { key: "isFinalFeedback", value: [false] },
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};
